"use client";

import { motion, useReducedMotion } from "framer-motion";
import { EASE_SMOOTH } from "@/components/motion/constants";
import { cn } from "@/lib/utils";

const LEAVES = [
  { left: "6%", top: "10%", rotate: -18, delay: 0, duration: 7, size: "h-5 w-5 md:h-7 md:w-7" },
  { left: "90%", top: "14%", rotate: 24, delay: 1.2, duration: 8, size: "h-6 w-6 md:h-8 md:w-8" },
  { left: "3%", top: "78%", rotate: 12, delay: 0.6, duration: 6.5, size: "h-5 w-5 md:h-6 md:w-6" },
  { left: "88%", top: "72%", rotate: -30, delay: 2, duration: 7.5, size: "h-6 w-6 md:h-7 md:w-7" },
] as const;

function LeafIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 2C8 8 4 10 4 14c0 3.3 2.7 6 6 6 1.5 0 2.9-.55 4-1.45C15.1 20 16.5 20.5 18 20.5c3.3 0 6-2.7 6-6.5 0-4-4-6-8-12-1 2-2.5 3.5-4 4.5z" />
    </svg>
  );
}

interface DonateFloatingLeavesProps {
  className?: string;
  tone?: "light" | "dark";
}

export function DonateFloatingLeaves({
  className,
  tone = "dark",
}: DonateFloatingLeavesProps) {
  const reduced = useReducedMotion();
  const leafColor =
    tone === "light"
      ? "text-white/20"
      : "text-[var(--programs-green)]/30";

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0 overflow-hidden",
        className
      )}
      aria-hidden
    >
      {!reduced &&
        LEAVES.map((leaf, i) => (
          <motion.div
            key={i}
            className={cn("absolute", leafColor)}
            style={{
              left: leaf.left,
              top: leaf.top,
              rotate: leaf.rotate,
            }}
            animate={{
              y: [0, -10, 0],
              rotate: [leaf.rotate, leaf.rotate + 6, leaf.rotate],
            }}
            transition={{
              duration: leaf.duration,
              delay: leaf.delay,
              repeat: Infinity,
              ease: EASE_SMOOTH,
            }}
          >
            <LeafIcon className={leaf.size} />
          </motion.div>
        ))}
    </div>
  );
}
